import { useState } from "react";
import { HealicMascot } from "@/components/HealicMascot";
import { CheckCircle2, ShieldCheck, Clock, Sparkles } from "lucide-react";

import { z } from "zod";
import { toast } from "sonner";
import { notifyForm } from "@/lib/notifyForm";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name").max(100),
  phone: z
    .string()
    .trim()
    .regex(/^[0-9+\-\s()]{7,20}$/, "Please enter a valid phone number"),
  email: z.string().trim().email("Please enter a valid email").max(255),
  city: z.string().min(1, "Please select a city"),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
});

const cities = [
  "Agra",
  "Haridwar",
  "Indirapuram, Ghaziabad",
  "Sohna Road, Gurugram",
  "Other",
];

export function BookingForm({ title, mascotSrc }: { title: string; mascotSrc?: string }) {
  const [city, setCity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name: String(fd.get("name") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      email: String(fd.get("email") ?? ""),
      city,
      notes: String(fd.get("notes") ?? ""),
    };
    const result = schema.safeParse(data);
    if (!result.success) {
      toast.error(result.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    const ok = await notifyForm({
      formType: "Appointment Request",
      source: title,
      fields: [
        { label: "Name", value: data.name },
        { label: "Phone", value: data.phone },
        { label: "Email", value: data.email },
        { label: "City / Location", value: data.city },
        { label: "Notes", value: data.notes },
      ],
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Thank you! Our team will contact you shortly.");
      (e.target as HTMLFormElement).reset();
      setCity("");
    } else {
      toast.error("Could not submit. Please call +91 95887 95887.");
    }
  };

  return (
    <section className="mx-auto max-w-5xl px-4 pb-20 lg:px-8">
      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
        <div className="grid lg:grid-cols-[300px_1fr]">
          {/* Side panel — desktop */}
          <div className="relative hidden overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/85 p-8 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent/30 blur-3xl" aria-hidden />
            <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-primary-foreground/10 blur-3xl" aria-hidden />

            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] backdrop-blur">
                <Sparkles className="h-3 w-3" /> Free Consultation
              </span>
              <h3 className="mt-4 font-display text-2xl font-bold leading-tight">
                Care that comes to you.
              </h3>
              <p className="mt-2 text-sm text-primary-foreground/80">
                Quick callback from our care team within minutes.
              </p>

              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>NABL-accredited labs & expert technicians</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>Home sample collection in 60 mins</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>Reports on email & WhatsApp</span>
                </li>
              </ul>
            </div>

            <div className="relative flex items-end justify-center pt-6">
              <div className="relative">
                <div className="absolute inset-0 -m-4 rounded-full bg-gradient-to-br from-white/95 to-white/80 blur-[2px]" aria-hidden />
                <div className="relative rounded-full bg-white/95 p-3 ring-1 ring-white/40 shadow-2xl">
                  {mascotSrc ? (
                    <img src={mascotSrc} alt="Healic mascot" loading="lazy" width={150} height={150} className="h-auto w-[150px] object-contain" draggable={false} />
                  ) : (
                    <HealicMascot size="lg" className="h-auto w-[150px]" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-10">
            <div className="flex items-center justify-center gap-3 lg:justify-start">
              <span className="lg:hidden">
                {mascotSrc ? (
                  <img src={mascotSrc} alt="Healic mascot" loading="lazy" width={96} height={96} className="h-16 w-16 object-contain sm:h-20 sm:w-20" draggable={false} />
                ) : (
                  <HealicMascot size="md" />
                )}
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                  {title}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Fill in your details and our team will contact you shortly.
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="mt-6 grid gap-3.5 sm:grid-cols-2">
              <Input aria-label="Full name" name="name" placeholder="Full Name" required maxLength={100} className="h-11 rounded-xl" />
              <Input aria-label="Phone number" name="phone" placeholder="Phone Number" required maxLength={20} className="h-11 rounded-xl" />
              <Input
                aria-label="Email address"
                name="email"
                type="email"
                placeholder="Email"
                required
                maxLength={255}
                className="h-11 rounded-xl"
              />
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger aria-label="City or location" className="h-11 w-full rounded-xl">
                  <SelectValue placeholder="City / Location" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                aria-label="Condition or medical notes"
                name="notes"
                placeholder="Condition / Medical Notes (optional)"
                maxLength={1000}
                className="min-h-24 rounded-xl sm:col-span-2"
              />

              <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:col-span-2 sm:flex-row sm:items-center">
                <p className="text-xs text-muted-foreground">
                  🔒 Confirmation sent on email & WhatsApp.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-xl bg-accent px-7 py-3 text-sm font-semibold text-accent-foreground shadow-cta transition hover:opacity-90 disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Request Call Back"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
